import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import SingleEvent from '../classes/events/SingleEvent';

// ============ Event Data Cache ============
const EVENT_CACHE_KEY = 'eventRealtimeDataCache';
const EVENT_CACHE_TTL = 1000 * 60 * 15; // 15 minut TTL

const getEventCache = () => {
  try {
    const cached = localStorage.getItem(EVENT_CACHE_KEY);
    if (!cached) return {};
    return JSON.parse(cached);
  } catch {
    return {};
  }
};

const saveEventCache = (cache) => {
  try {
    // Ogranicz rozmiar cache
    const entries = Object.entries(cache);
    if (entries.length > 50) {
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      entries.slice(0, entries.length - 50).forEach(([k]) => delete cache[k]);
    }
    localStorage.setItem(EVENT_CACHE_KEY, JSON.stringify(cache));
  } catch (err) {
    console.warn('Failed to save event cache:', err);
  }
};

const getCachedEventData = (eventId) => {
  if (!eventId) return null;
  const cache = getEventCache();
  const entry = cache[eventId];
  if (!entry) return null;
  if (Date.now() - entry.timestamp > EVENT_CACHE_TTL) {
    delete cache[eventId];
    saveEventCache(cache);
    return null;
  }
  return entry.data;
};

const setCachedEventData = (eventId, data) => {
  if (!eventId) return;
  const cache = getEventCache();
  cache[eventId] = { data, timestamp: Date.now() };
  saveEventCache(cache);
};

/**
 * Hook do zarządzania danymi eventu w czasie rzeczywistym
 * Automatycznie aktualizuje liczbę uczestników, status eventu itp.
 * Z wbudowanym cache'owaniem dla szybszego ładowania
 * 
 * @param {string} eventId - ID eventu
 * @param {Object} initialEvent - Początkowe dane eventu (opcjonalne)
 * @returns {Object} { event, participantsCount, isLoading, error }
 */
// Eksportowane funkcje do zarządzania cache z zewnątrz
export const invalidateEventRealtimeCache = (eventId) => {
  if (!eventId) return;
  const cache = getEventCache();
  delete cache[eventId];
  saveEventCache(cache);
};

export const clearAllEventRealtimeCache = () => {
  try {
    localStorage.removeItem(EVENT_CACHE_KEY);
  } catch (err) {
    console.warn('Failed to clear event realtime cache:', err);
  }
};

export function useEventRealtimeData(eventId, initialEvent = null) {
  // Spróbuj załadować z cache przy inicjalizacji
  const cachedData = eventId ? getCachedEventData(eventId) : null;
  
  const [event, setEvent] = useState(initialEvent || cachedData?.event || null);
  const [host, setHost] = useState(cachedData?.host || null);
  const [participantsCount, setParticipantsCount] = useState(cachedData?.participantsCount || 0);
  const [participants, setParticipants] = useState(cachedData?.participants || []);
  const [isLoading, setIsLoading] = useState(!initialEvent && !cachedData);
  const [error, setError] = useState(null);
  const channelRef = useRef(null);
  const hasLoadedFromServer = useRef(false);
  const channelInstanceIdRef = useRef(`rt_${Math.random().toString(36).slice(2, 10)}`);

  // Ładowanie danych z serwera
  useEffect(() => {
    if (!eventId) return;

    const loadInitialData = async () => {
      try {
        // Nie pokazuj loading jeśli mamy dane z cache
        if (!event && !cachedData) {
          setIsLoading(true);
        }
        setError(null);

        let eventData = initialEvent;

        // Załaduj dane eventu jeśli nie zostały przekazane
        if (!eventData) {
          const { data, error: eventError } = await SingleEvent.loadEvent(eventId);
          if (eventError) throw eventError;
          eventData = data;
          setEvent(data);
        }

        // Załaduj hosta
        let hostData = null;
        if (eventData?.host_id) {
          const { data } = await SingleEvent.loadHost(eventData.host_id);
          hostData = data;
          setHost(data);
        }

        // Załaduj liczbę uczestników i uczestników (dla awatarów)
        const [countResult, participantsResult] = await Promise.all([
          SingleEvent.loadParticipantsCount(eventId),
          SingleEvent.loadParticipants(eventId)
        ]);
        
        if (countResult.error) throw countResult.error;
        setParticipantsCount(countResult.count || 0);
        
        let participantsData = [];
        if (!participantsResult.error) {
          participantsData = participantsResult.data || [];
          setParticipants(participantsData);
        }

        // Zapisz do cache
        hasLoadedFromServer.current = true;
        setCachedEventData(eventId, {
          event: eventData,
          host: hostData,
          participantsCount: countResult.count || 0,
          participants: participantsData
        });
      } catch (err) {
        console.error('Error loading event data:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Subskrypcja real-time dla zmian w uczestnikach
  useEffect(() => {
    if (!eventId) return;

    // Clean up existing subscription
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }

    // Subscribe to changes in event_participants
    const channelName = `event-realtime-${eventId}-${channelInstanceIdRef.current}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'event_participants',
          filter: `event_id=eq.${eventId}`
        },
        async (payload) => {
          // Przeładuj liczbę uczestników i listę uczestników gdy nastąpi zmiana
          const [countResult, participantsResult] = await Promise.all([
            SingleEvent.loadParticipantsCount(eventId),
            SingleEvent.loadParticipants(eventId)
          ]);
          
          if (!countResult.error && countResult.count !== null) {
            setParticipantsCount(countResult.count);
          }
          
          const participantsData = participantsResult.data || [];
          if (!participantsResult.error) {
            setParticipants(participantsData);
          }

          // Zaktualizuj cache
          const cached = getCachedEventData(eventId);
          if (cached) {
            setCachedEventData(eventId, {
              ...cached,
              participantsCount: countResult.count || cached.participantsCount,
              participants: participantsData
            });
          }
        }
      )
      .subscribe();

    channelRef.current = channel;

    // Cleanup
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [eventId]);

  // Oblicz dostępne miejsca
  const availableSpots = event ? event.max_participants - participantsCount : 0;
  const isFull = availableSpots <= 0;

  return {
    event,
    host,
    participantsCount,
    participants,
    availableSpots,
    isFull,
    isLoading,
    error
  };
}

export default useEventRealtimeData;
